import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/components/DashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import ProductsPage from "@/pages/ProductsPage";
import ShippingPage from "@/pages/ShippingPage";
import ShipmentCostingPage from "@/pages/ShipmentCostingPage";
import ShipmentCalendarViewPage from "@/pages/ShipmentCalendarViewPage";
import NotesPage from "@/pages/NotesPage";
import LedgerPage from "@/pages/LedgerPage";
import SalesPage from "@/pages/SalesPage";
import SalesRawDataViewPage from "@/pages/SalesRawDataViewPage";
import LedgerReconciliationPage from "@/pages/LedgerReconciliationPage";
import LedgerPreviewPage from "@/pages/LedgerPreviewPage";
import PartnerCollectionsPage from "@/pages/PartnerCollectionsPage";
import ClientPriceListPage from "@/pages/ClientPriceListPage";
import UserManagementPage from "@/pages/UserManagementPage";
import PartnerDashboard from "@/pages/PartnerDashboard";
import AgentDashboard from "@/pages/AgentDashboard";
import LogsPage from "@/pages/LogsPage";
import ErrorResolutionPage from "@/pages/ErrorResolutionPage";
import UploadCenterPage from "@/pages/UploadCenterPage";
import YearSummaryPreviewPage from "@/pages/YearSummaryPreviewPage";
import PaymentVerificationPage from "@/pages/PaymentVerificationPage";
import RawFundsHistoryPage from "@/pages/RawFundsHistoryPage";
import ClientBalancePage from "@/pages/ClientBalancePage";
import AdvanceLedgerPage from "@/pages/AdvanceLedgerPage";
import ReportsPage from "@/pages/ReportsPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import SupportPage from "@/pages/SupportPage";
import OutstandingBalanceReportPage from "@/pages/OutstandingBalanceReportPage";
import AgingSummaryReportPage from "@/pages/AgingSummaryReportPage";
import SalesProfitReportPage from "@/pages/SalesProfitReportPage";
import SalesComparisonReportPage from "@/pages/SalesComparisonReportPage";
import ErrorReconciliationReportPage from "@/pages/ErrorReconciliationReportPage";
import PendingSalesReportPage from "@/pages/PendingSalesReportPage";
import PendingAutoGenerateReportPage from "@/pages/PendingAutoGenerateReportPage";
import PendingShipmentCostingReportPage from "@/pages/PendingShipmentCostingReportPage";
import MonthDetailReportPage from "@/pages/MonthDetailReportPage";
import SectionPrintPage from "@/pages/SectionPrintPage";
import PendingSalesByClientReportPage from "@/pages/PendingSalesByClientReportPage";
import ManagerPerformancePage from "@/pages/ManagerPerformancePage";
import CRMPage from "@/pages/CRMPage";
import CustomerReportPage from "@/pages/CustomerReportPage";
import ClientTimelinePage from "@/pages/ClientTimelinePage";
import PerformanceReportsPage from "@/pages/PerformanceReportsPage";
import AllFollowUpsPage from "@/pages/AllFollowUpsPage";
import DataManagementPage from "@/pages/DataManagementPage";
import PrintPreviewPage from "@/pages/PrintPreviewPage";
import FinancialOverviewPage from "@/pages/FinancialOverviewPage";
import FinancialOverviewPrintPage from "@/pages/FinancialOverviewPrintPage";
import HealthDashboardPage from "@/pages/HealthDashboardPage";
import FavoritesPage from "@/pages/FavoritesPage";
import DocumentsPage from "@/pages/DocumentsPage";
import PasswordVaultPage from "@/pages/PasswordVaultPage";
import ClientReportViewer from "@/pages/ClientReportViewer";
import { PrintSettingsProvider } from "@/contexts/PrintSettingsContext";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        logout();
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check role if specified
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "partner") {
      return <Navigate to="/partner" replace />;
    } else if (user.role === "agent") {
      return <Navigate to="/agent" replace />;
    } else if (user.role === "uploader") {
      return <Navigate to="/upload-center" replace />;
    } else if (user.role === "error_manager") {
      return <Navigate to="/errors" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  
  if (user?.role === "partner") {
    return <Navigate to="/partner" replace />;
  } else if (user?.role === "agent") {
    return <Navigate to="/agent" replace />;
  } else if (user?.role === "error_manager") {
    return <Navigate to="/errors" replace />;
  } else if (user?.role === "uploader") {
    return <Navigate to="/upload-center" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

// Route guard that checks if a user has access to a route based on allowed_modules
const RouteGuard = ({ children, restrictedRoles = [], requiredModule = null }) => {
  const { user } = useAuth();
  
  // Super admin always has access
  if (user?.role === "super_admin") {
    return children;
  }
  
  // If requiredModule is specified, check if user has it in allowed_modules
  if (requiredModule) {
    const userModules = user?.allowed_modules || [];
    if (userModules.includes(requiredModule) || userModules.includes("all")) {
      return children;
    }
  }
  
  // Fall back to role-based restriction (for backward compatibility)
  if (restrictedRoles.includes(user?.role)) {
    if (user?.role === "uploader") {
      return <Navigate to="/upload-center" replace />;
    } else if (user?.role === "error_manager") {
      return <Navigate to="/errors" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <PrintSettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            
            {/* Partner Dashboard - Standalone */}
            <Route
              path="/partner"
              element={
                <ProtectedRoute allowedRoles={["partner", "super_admin"]}>
                  <PartnerDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Agent Dashboard - Standalone */}
            <Route
              path="/agent"
              element={
                <ProtectedRoute allowedRoles={["agent", "super_admin"]}>
                  <AgentDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Print routes - outside DashboardLayout (no sidebar) */}
            <Route path="/financial-overview/print" element={<ProtectedRoute><FinancialOverviewPrintPage /></ProtectedRoute>} />
            <Route path="/reports/client/:reportType/:clientCode" element={<ProtectedRoute><ClientReportViewer /></ProtectedRoute>} />

            {/* Admin Routes with Sidebar */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<RoleBasedRedirect />} />
              <Route path="dashboard" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><DashboardPage /></RouteGuard>} />
              <Route path="crm" element={<RouteGuard requiredModule="crm"><CRMPage /></RouteGuard>} />
              <Route path="crm/customer-report/:clientCode" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><CustomerReportPage /></RouteGuard>} />
              <Route path="crm/all-follow-ups" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><AllFollowUpsPage /></RouteGuard>} />
              <Route path="crm/client/:clientCode/timeline" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><ClientTimelinePage /></RouteGuard>} />
              <Route path="products" element={<RouteGuard restrictedRoles={["uploader"]}><ProductsPage /></RouteGuard>} />
              <Route path="shipping" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><ShippingPage /></RouteGuard>} />
              <Route path="costing" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><ShipmentCostingPage /></RouteGuard>} />
              <Route path="costing/calendar" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><ShipmentCalendarViewPage /></RouteGuard>} />
              <Route path="ledger" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><LedgerPage /></RouteGuard>} />
              <Route path="sales" element={<RouteGuard requiredModule="sales_profit"><SalesPage /></RouteGuard>} />
              <Route path="sales/raw-data-view" element={<RouteGuard requiredModule="sales_profit"><SalesRawDataViewPage /></RouteGuard>} />
              <Route path="ledger-reconciliation" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><LedgerReconciliationPage /></RouteGuard>} />
              <Route path="ledger-preview" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><LedgerPreviewPage /></RouteGuard>} />
              <Route path="partner-collections" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><PartnerCollectionsPage /></RouteGuard>} />
              <Route path="notes" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><NotesPage /></RouteGuard>} />
              <Route path="users" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><UserManagementPage /></RouteGuard>} />
              <Route path="logs" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><LogsPage /></RouteGuard>} />
              <Route path="errors" element={<RouteGuard requiredModule="error_resolution"><ErrorResolutionPage /></RouteGuard>} />
              <Route path="reports" element={<RouteGuard requiredModule="reports"><ReportsPage /></RouteGuard>} />
              <Route path="reports/outstanding-balance" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><OutstandingBalanceReportPage /></RouteGuard>} />
              <Route path="reports/aging-summary" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><AgingSummaryReportPage /></RouteGuard>} />
              <Route path="reports/sales-profit" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><SalesProfitReportPage /></RouteGuard>} />
              <Route path="reports/sales-comparison" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><SalesComparisonReportPage /></RouteGuard>} />
              <Route path="reports/error-reconciliation" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><ErrorReconciliationReportPage /></RouteGuard>} />
              <Route path="reports/pending-sales" element={<RouteGuard restrictedRoles={["error_manager"]}><PendingSalesReportPage /></RouteGuard>} />
              <Route path="reports/pending-auto-generate" element={<RouteGuard restrictedRoles={["error_manager"]}><PendingAutoGenerateReportPage /></RouteGuard>} />
              <Route path="reports/pending-shipment-costing" element={<RouteGuard restrictedRoles={["error_manager"]}><PendingShipmentCostingReportPage /></RouteGuard>} />
              <Route path="reports/month-detail" element={<RouteGuard restrictedRoles={["error_manager"]}><MonthDetailReportPage /></RouteGuard>} />
              <Route path="reports/section-print" element={<RouteGuard restrictedRoles={["error_manager"]}><SectionPrintPage /></RouteGuard>} />
              <Route path="reports/pending-sales-by-client" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><PendingSalesByClientReportPage /></RouteGuard>} />
              <Route path="reports/manager-performance" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><ManagerPerformancePage /></RouteGuard>} />
              <Route path="reports/performance" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><PerformanceReportsPage /></RouteGuard>} />
              <Route path="upload-center" element={<UploadCenterPage />} />
              <Route path="year-summary-preview" element={<YearSummaryPreviewPage />} />
              <Route path="payment-verification" element={<PaymentVerificationPage />} />
              <Route path="raw-funds-history" element={<RawFundsHistoryPage />} />
              <Route path="client-balance" element={<ClientBalancePage />} />
              <Route path="advance-ledger" element={<AdvanceLedgerPage />} />
              <Route path="financial-overview" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><FinancialOverviewPage /></RouteGuard>} />
              <Route path="help" element={<HelpCenterPage />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="data-management" element={<RouteGuard restrictedRoles={["uploader", "error_manager", "agent", "partner"]}><DataManagementPage /></RouteGuard>} />
              <Route path="health" element={<RouteGuard restrictedRoles={["uploader", "error_manager", "agent", "partner"]}><HealthDashboardPage /></RouteGuard>} />
              <Route path="favorites" element={<FavoritesPage />} />
              <Route path="documents" element={<RouteGuard restrictedRoles={["uploader", "error_manager", "partner", "agent"]}><DocumentsPage /></RouteGuard>} />
              <Route path="vault" element={<RouteGuard restrictedRoles={["uploader", "error_manager", "partner", "agent"]}><PasswordVaultPage /></RouteGuard>} />
              <Route path="price-list/:clientCode" element={<RouteGuard restrictedRoles={["uploader", "error_manager"]}><ClientPriceListPage /></RouteGuard>} />
              <Route path="print-preview" element={<PrintPreviewPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </PrintSettingsProvider>
  );
}

export default App;
